import { useTranslation } from "react-i18next";
import { Order } from "/app/src/models";
import Box from "/app/src/components/generic/components/box";
import { Button, Divider } from "@heroui/react";
import RedButton from "/app/src/components/HeroUi/RedButton";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { processService } from "/app/src/services/processService";
import {
  getProcessingStatusResponse,
  queueOrderResponse,
} from "/app/src/types/processing";
import { useCallback, useEffect, useState } from "react";
import { handlePromiseError } from "/app/src/helpers/api";
import { toast } from "sonner";

/**
 * Component that displays the lines of an order.
 *
 * @param {{ order: Order }} props - The properties for the component.
 * @param {Order} props.order - The order object containing order_lines to display.
 * @returns {JSX.Element} The rendered component displaying each order line.
 */
export default function Lines({
  order,
  setMessage,
}: {
  order: Order;
  setMessage: React.Dispatch<
    React.SetStateAction<"Processing" | "Error" | "Success" | "">
  >;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [buttonStates, setButtonStates] = useState({
    start: { isDisabled: false, isLoading: false },
    cancel: { isDisabled: false, isLoading: false },
    finish: { isDisabled: false, isLoading: false },
  });

  useEffect(() => {
    if (order?.status === "processed") {
      setMessage("Success");
      setButtonStates((prev) => ({
        ...prev,
        start: { isDisabled: true, isLoading: false },
      }));
    }
  }, [order?.status, setMessage]);

  const { data: processingResponse, refetch: startProcessing } = useQuery({
    queryKey: ["startProcessing", order.id],
    queryFn: () => {
      return processService.queueOrder(order.id);
    },
    initialData: {
      queued: false,
      processId: null,
      message: "",
    } as queueOrderResponse,
    enabled: false,
    select: (data) => data,
  });

  const { mutateAsync: finishProcessing } = useMutation({
    mutationFn: (processId: string) => {
      return processService.finishProcess(processId).then(handlePromiseError);
    },
  });

  const handleFinishProcessing = useCallback(() => {
    setButtonStates((prev) => ({
      ...prev,
      cancel: { isDisabled: true, isLoading: false },
      finish: { isDisabled: true, isLoading: true },
    }));

    finishProcessing(processingResponse?.processId).then((response) => {
      if (response?.finished) {
        setMessage("Success");
        queryClient.setQueryData(["startProcessing", order.id], {
          queued: false,
        });
        setButtonStates((prev) => ({
          ...prev,
          start: { isDisabled: true, isLoading: false },
          cancel: { isDisabled: false, isLoading: false },
          finish: { isDisabled: false, isLoading: false },
        }));
      }
    });
  }, [
    finishProcessing,
    order.id,
    processingResponse?.processId,
    queryClient,
    setMessage,
  ]);

  const { mutateAsync: cancelProcessing } = useMutation({
    mutationFn: (processId: string) => {
      return processService.cancelProcess(processId).then(handlePromiseError);
    },
    onSuccess: () => {
      queryClient.setQueryData(["startProcessing", order.id], {
        queued: false,
      });
    },
  });

  const handleCancelProcessing = useCallback(() => {
    setButtonStates((prev) => ({
      ...prev,
      cancel: { isDisabled: true, isLoading: true },
      finish: { isDisabled: true, isLoading: false },
    }));

    cancelProcessing(processingResponse?.processId).then(() => {
      setMessage("");
      setButtonStates((prev) => ({
        ...prev,
        start: { isDisabled: false, isLoading: false },
        cancel: { isDisabled: false, isLoading: false },
        finish: { isDisabled: false, isLoading: false },
      }));
    });
  }, [cancelProcessing, processingResponse?.processId, setMessage]);

  const handleStartProcessing = useCallback(() => {
    if (order?.incompleteAllocations) {
      toast.error(
        t(
          "translation:order_incomplete_allocations",
          "Order is unable to process due to incomplete allocations.",
        ),
      );
      return;
    }
    setButtonStates((prev) => ({
      ...prev,
      start: { isDisabled: true, isLoading: true },
    }));
    startProcessing().then((response) => {
      if (response.data?.queued) {
        setMessage("Processing");
      } else {
        setMessage("Error");
        setButtonStates((prev) => ({
          ...prev,
          start: { isDisabled: false, isLoading: false },
        }));
      }
    });
  }, [setMessage, startProcessing, t, order?.incompleteAllocations]);

  const { data: processingStatus } = useQuery({
    queryKey: ["processingStatus", processingResponse?.processId],
    queryFn: () => {
      return processService.getProcessingStatus(processingResponse?.processId);
    },
    initialData: {
      status: "",
      message: "",
      inputNeeded: "",
    } as getProcessingStatusResponse,
    refetchInterval: 5000,
    enabled: Boolean(processingResponse?.processId),
    select: (data) => {
      console.info("Processing Status:", data);
      return data;
    },
  });
  return (
    <Box>
      <div className="flex justify-between items-center">
        <h2 className="text-[18px] mb-0">{t("translation:order_lines")}</h2>
        <div className="flex space-x-4">
          {processingResponse?.queued ? (
            <RedButton
              onPress={handleCancelProcessing}
              radius="none"
              variant="bordered"
              isDisabled={buttonStates.cancel.isDisabled}
              isLoading={buttonStates.cancel.isLoading}
            >
              {t("translation:cancel")}
            </RedButton>
          ) : (
            <Button
              onPress={handleStartProcessing}
              radius="none"
              isDisabled={buttonStates.start.isDisabled}
              isLoading={buttonStates.start.isLoading}
              color="primary"
            >
              {t("translation:start_processing")}
            </Button>
          )}
          {processingResponse?.queued && (
            <Button
              onPress={handleFinishProcessing}
              radius="none"
              color="primary"
              isDisabled={buttonStates.finish.isDisabled}
              isLoading={buttonStates.finish.isLoading}
            >
              {t("translation:finish_processing")}
            </Button>
          )}
        </div>
      </div>
      <Divider className="my-3" />

      {order?.order_lines?.length > 0 &&
        order?.order_lines?.map((line) => (
          <Box key={line.id} className="mb-3 p-3 border rounded">
            <div className="flex items-center space-x-8">
              <h2 className="mb-0 text-[18px] w-[300px] truncate">
                {t("translation:material")}: {line?.materialName}
              </h2>
              <h2 className="mb-0 text-[18px]">
                {t("translation:quantity")}: {line?.quantity}
              </h2>
            </div>
          </Box>
        ))}
    </Box>
  );
}
